/* eslint-disable react-hooks/exhaustive-deps */
import { noop } from "lodash";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { bindActionCreators } from "redux";
import { Dimmer, Loader } from "semantic-ui-react";

import Segments from "components/navigation/segments/Segments";
import { SwitchEntityTypes } from "enums/switchEntityTypes";
import AssociatedPacks from "features/Licensing/Packs/AssociatedPacks/AssociatedPacks";
import ModalTypes from "../../../../components/modal/ModalTypes";
import PublishConfirmationModal, {
  type EntityState,
} from "../../../../components/modal/PublishConfirmationModal/PublishConfirmationModal";
import RevertConfirmationModalTs from "../../../../components/modal/RevertConfirmationModalTs";
import {
  PublishedStatusTypes,
  RolePermissions,
  RouteNames,
  TemplateTypes,
  UsersGroupsContext,
} from "../../../../enums";
import { useRtn } from "../../../../hooks/useRtn";
import { useWizardStepsManager } from "../../../../hooks/useWizardStepsManager";
import { bindAction, type ISegment, type NotifyStepSettings } from "../../../../interfaces";
import { default as ModalManager } from "../../../../utils/ModalManager";
import Observable from "../../../../utils/Observable";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import Restricted from "../../../Application/Restricted";
import * as rtnEvents from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import { NotifyStepConfig, type ShouldNotifyWithProgressIds } from "../../../SystemNotifications/types";
import SurveyPerformance from "../Performance/SurveyPerformance";
import * as commonSurveyActions from "../state/actions/commonSurveyActions";
import * as surveyContentValidationActions from "../state/actions/surveyContentValidationActions";
import * as surveyDetailsActions from "../state/actions/surveyDetailsActions";
import * as surveyEntityStateActions from "../state/actions/surveyEntityStateActions";
import { surveysStateSelector } from "../state/surveyReducer";
import { fetchSurveyDependencies } from "../state/thunks/surveyCommonThunk";
import { default as Configure } from "../SurveyDetails/Configure/Configure";
import SettingsTab from "../SurveyDetails/Settings/SettingsTab";
import SurveyContent from "../SurveyDetails/SurveyContent/SurveyContent";
import EditSurveyDetailsHeader from "./EditSurveyDetailsHeader/EditSurveyDetailsHeader";
import { default as EditSurveySubheaderContainer } from "./EditSurveySubheaderContainer";
import EditSurveyPeople from "./People/EditSurveyPeople";
import { EditSurveySteps, type EnumDictionary, showLastModified, showReportLastUpdated } from "./types";

export type EditSurveyPropsAll = PropsFromRedux;

export const EditSurvey = (props: EditSurveyPropsAll) => {
  const surveysOverviewUrl = `/${RouteNames.contentSurveys}`;
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const surveyId = Number(params.id);
  const pageIndexes = [
    EditSurveySteps.Performance,
    EditSurveySteps.Configuration,
    EditSurveySteps.Settings,
    EditSurveySteps.Content,
    EditSurveySteps.People,
    EditSurveySteps.AssociatedPacks,
  ];
  const [wizardPages, { goToPage, goBack }, subscribeOnActiveIndexChanged] = useWizardStepsManager(pageIndexes);
  const revertModalManager = useMemo(
    () =>
      new ModalManager(
        ModalTypes.RevertSurvey,
        () => props.entityStateActions.fetchDiscardSurveyEntity(surveyId),
        false,
        props.userId,
      ),
    [surveyId, props.userId],
  );
  const onDiscardedObserver = useMemo(() => new Observable<() => void>(), []);
  const onTriggerPublishContentObserver = useMemo(() => new Observable(), []);

  const [activeTab, setActiveTab] = useState(0);

  const isPublishedAndNotPurchased = !props.survey.isDraft && !props.survey.isPurchased;
  const [usersGroupsContext, setUsersGroupsContext] = useState(UsersGroupsContext.Groups);
  const loadSurveyInfo = () => {
    if (!props.isSurveyInfoLoaded) {
      props.detailsActions.getSurvey(surveyId);
    }
  };

  const loadSurveyContent = () => {
    if (!props.isContentLoaded) {
      props.detailsActions.getSurveyContent(surveyId);
    }
    loadSurveyInfo();
  };

  const pages: EnumDictionary<EditSurveySteps, ISegment> = useMemo(() => {
    return {
      [EditSurveySteps.Performance]: {
        to: "",
        label: "Performance",
        onClick: () => {
          goToPage(EditSurveySteps.Performance);
        },
      },
      [EditSurveySteps.Configuration]: {
        to: "configuration",
        label: "Configuration",
        onClick: () => {
          goToPage(EditSurveySteps.Configuration);
        },
      },
      [EditSurveySteps.Settings]: {
        to: "settings",
        label: "Settings",
        onClick: () => {
          goToPage(EditSurveySteps.Settings);
        },
      },
      [EditSurveySteps.Content]: {
        to: "content",
        label: "Content",
        onClick: () => {
          goToPage(EditSurveySteps.Content);
        },
      },
      [EditSurveySteps.People]: {
        to: "people",
        label: "People",
        onClick: () => {
          goToPage(EditSurveySteps.People);
        },
      },
      [EditSurveySteps.AssociatedPacks]: {
        to: "packs",
        label: "Associated Packs",
        onClick: () => {
          goToPage(EditSurveySteps.AssociatedPacks);
        },
      },
    };
  }, [goToPage]);

  const [isValid, setIsValid] = useState(true);
  useLayoutEffect(() => {
    const handler = (_: any, activeStepIndex: EditSurveySteps) => {
      pages[activeStepIndex].init?.();
      setActiveTab(activeStepIndex);
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [pages, subscribeOnActiveIndexChanged]);

  useEffect(() => {
    loadSurveyContent();
    return () => {
      props.onUnmount();
      revertModalManager.dispose();
    };
  }, []);

  const onPublishedOrDiscardEvent = () => {
    onDiscardedObserver.notify();
    props.onDiscard(surveyId);
  };

  useRtn(
    [rtnEvents.SurveyPublishSuccess, rtnEvents.SurveyDiscardSuccess, rtnEvents.SurveyEditSuccess],
    onPublishedOrDiscardEvent,
  );

  const onErrorClick = () => {
    const redirectTo = `${surveysOverviewUrl}/${surveyId}/${pages[EditSurveySteps.Content].to}`;
    if (!location.pathname.includes(redirectTo)) {
      navigate(redirectTo);
      goToPage(EditSurveySteps.Content);
    }
  };

  const back = () => {
    if (!goBack()) {
      return surveysOverviewUrl;
    }
  };

  const onRevertClick = () => revertModalManager.execute();

  const renderRevertModal = () =>
    props.hasBeenPublished && <RevertConfirmationModalTs modalManager={revertModalManager} />;

  const onPublishTrigger = async () => {
    const { survey, hasPacks } = props;

    const flowsCount = survey.flows.length;
    const entityState = {
      itemsCount: flowsCount,
      entityId: surveyId,
      hasBeenPublished: survey.hasBeenPublished,
      templateType: survey.hasBeenPublished ? TemplateTypes.SurveyChanged : TemplateTypes.AddedToSurvey,
      notifyConfigType: survey.hasBeenPublished ? NotifyStepConfig.WithProgress : NotifyStepConfig.Default,
      entityType: SwitchEntityTypes.Survey,
    };

    triggerPublish(hasPacks, entityState);
  };

  const triggerPublish = (condition: boolean, entityState: EntityState) => {
    return condition ? onTriggerPublishContentObserver.notify(noop, entityState) : handlePublish(surveyId);
  };

  const handlePublish = (
    id: number,
    notificationSettings?: NotifyStepSettings,
    peopleToNotifyTypes?: ShouldNotifyWithProgressIds[],
  ) => {
    props.validateActions.tryPublish(id, notificationSettings, peopleToNotifyTypes);
  };

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission: boolean) => (
        <div className="nested-content edit-assessment scrollable-content">
          <EditSurveyDetailsHeader
            surveyId={surveyId}
            surveysOverviewUrl={surveysOverviewUrl}
            title={props.title}
            canBeEdited={isPublishedAndNotPurchased}
            crudPermission={hasPermission}
            canBePublished={props.isDraft && props.contentResolved}
            isRevertVisible={props.hasBeenPublished}
            invalidFormDetails={!isValid}
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!props.isDraft, props.hasBeenPublished)}
            hideEntityStateButtons={activeTab === EditSurveySteps.People}
            onErrorClick={onErrorClick}
            onPublishClick={onPublishTrigger}
            entityStateActions={{
              onRevert: onRevertClick,
            }}
            back={back}
            canAddPacks={isPublishedAndNotPurchased}
            showAddPacksButton={activeTab === EditSurveySteps.AssociatedPacks}
          />
          {renderRevertModal()}
          <PublishConfirmationModal
            onTriggerPublishContentObserver={onTriggerPublishContentObserver}
            onConfirmAction={handlePublish}
            contentType="survey"
            dependencyType="flow"
          />
          <Dimmer active={props.isLoading} inverted>
            <Loader />
          </Dimmer>
          <EditSurveySubheaderContainer
            publishedStatus={PublishedStatusTypes.ConvertToPublishedStatusType(!props.isDraft)}
            isUpdateInProgress={props.isDraft}
            lastModifiedDateTime={props.survey.dateModified}
            showLastModified={showLastModified.has(activeTab)}
            showReportLastUpdated={showReportLastUpdated.has(activeTab)}
          >
            <Segments to={`${surveysOverviewUrl}/${surveyId}`}>
              <Segments.Segment {...pages[EditSurveySteps.Performance]} />
              <Segments.Segment {...pages[EditSurveySteps.Configuration]} />
              <Segments.Segment {...pages[EditSurveySteps.Settings]} />
              <Segments.Segment {...pages[EditSurveySteps.Content]} />
              <Segments.Segment {...pages[EditSurveySteps.People]} />
              <Segments.Segment {...pages[EditSurveySteps.AssociatedPacks]} />
            </Segments>
          </EditSurveySubheaderContainer>
          <div className="scrollable-content">
            <Routes>
              <Route
                path={pages[EditSurveySteps.Performance].to}
                element={
                  <div className="edit-form">
                    <SurveyPerformance
                      acceptHandlers={wizardPages[EditSurveySteps.Performance]}
                      surveyId={surveyId}
                      surveyTitle={props.title}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditSurveySteps.Configuration].to}
                element={
                  <div className="edit-form">
                    <Configure
                      entityId={surveyId}
                      acceptHandlers={wizardPages[EditSurveySteps.Configuration]}
                      subscribeOnDiscarded={onDiscardedObserver.subscribe}
                      unSubscribeOnDiscarded={onDiscardedObserver.unsubscribe}
                      disabled={!props.isDraft}
                      mode="edit"
                      onIsValidChange={setIsValid}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditSurveySteps.Settings].to}
                element={
                  <div className="edit-form">
                    <SettingsTab entityId={surveyId} acceptHandlers={wizardPages[EditSurveySteps.Settings]} />
                  </div>
                }
              />
              <Route
                path={pages[EditSurveySteps.Content].to}
                element={
                  <div className="edit-form">
                    <SurveyContent
                      entityId={surveyId}
                      acceptHandlers={wizardPages[EditSurveySteps.Content]}
                      subscribeOnDiscarded={onDiscardedObserver.subscribe}
                      unSubscribeOnDiscarded={onDiscardedObserver.unsubscribe}
                      disabled={!props.isDraft}
                    />
                  </div>
                }
              />
              <Route
                path={pages[EditSurveySteps.People].to}
                element={
                  <EditSurveyPeople
                    entityId={surveyId}
                    acceptHandlers={wizardPages[EditSurveySteps.People]}
                    usersGroupsContext={usersGroupsContext}
                    onContextChanged={setUsersGroupsContext}
                  />
                }
              />
              <Route
                path={pages[EditSurveySteps.AssociatedPacks].to}
                element={
                  <AssociatedPacks
                    acceptHandlers={wizardPages[EditSurveySteps.AssociatedPacks]}
                    contentType={"Survey"}
                    contentId={surveyId}
                    canAddPacks={isPublishedAndNotPurchased}
                    permissions={[RolePermissions.AssetsCreate]}
                  />
                }
              />
              <Route path="*" element={<Navigate to="../" replace />} />
            </Routes>
          </div>
        </div>
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const surveys = surveysStateSelector(state);
  const base = surveys.base;
  return {
    title: base.surveysDetailsReducer.title,
    survey: base.surveysDetailsReducer.survey,
    isDraft: base.surveysDetailsReducer.isDraft,
    isLoading: base.surveysDetailsReducer.isLoading,
    isSurveyInfoLoaded: base.surveysDetailsReducer.isSurveyInfoLoaded,
    isContentLoaded: base.surveysDetailsReducer.isContentLoaded,
    contentResolved: base.surveysContentValidationReducer.resolved,
    hasBeenPublished: base.surveysDetailsReducer.survey.hasBeenPublished,
    userId: Number(state.userProfile.id),
    hasPacks: !!base.surveysDetailsReducer.settings.packs.length,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  entityStateActions: bindActionCreators(surveyEntityStateActions, dispatch),
  detailsActions: bindActionCreators(surveyDetailsActions, dispatch),
  validateActions: bindActionCreators(surveyContentValidationActions, dispatch),
  fetchDependencies: bindAction(fetchSurveyDependencies, dispatch),
  onDiscard: (id: number) => {
    dispatch(commonSurveyActions.discardAction());
    dispatch(surveyDetailsActions.getSurvey(id));
    dispatch(surveyDetailsActions.getSurveyContent(id));
  },
  onUnmount: () => {
    dispatch(surveyDetailsActions.clearSurvey());
    dispatch(surveyContentValidationActions.resetAction());
    dispatch(surveyEntityStateActions.resetSurveyEntityState());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditSurvey);
export default ConnectedComponent;
